import { useCallback, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import { Bundle, Observation } from "@medplum/fhirtypes";
import { useMedplum, useSubscription } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { observationHasSharedPostpartumRelatedCode } from "@/encounters/components/EncounterLabor/constants/shared-postpartum-related-observation-codes";
import { LaborEncounterLiveObservationsContext } from "@/encounters/components/EncounterLabor/contexts/LaborEncounterLiveObservationsContext";
import { useNewbornFlowsheetSubject } from "@/encounters/components/EncounterLabor/hooks/useNewbornFlowsheetSubject";
import { useRelatedPatientPostpartum } from "@/encounters/components/EncounterLabor/hooks/useRelatedPatientPostpartum";
import {
  filterObservationsForPostpartumRedux,
  mergeIncomingObservationsIntoCache,
} from "@/encounters/components/EncounterLabor/utils/postpartum-observation-processing";
import {
  fetchCanonicalNewbornPostpartumObservations,
  searchEncounterObservationsForPatient,
} from "@/encounters/components/EncounterLabor/utils/related-patient-observations";
import { RootState } from "@/store";
import { selectBirthDateTime, selectIsOptimisticBirth } from "@/store/slices/birth";
import {
  clearPostpartumObservations,
  loadPostpartumObservations,
  selectPostpartumObservations,
} from "@/store/slices/postpartum-observations";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

export function usePostpartumObservations(encounterId?: string, clientId?: string) {
  const live = useContext(LaborEncounterLiveObservationsContext);
  const liveRefreshObservations = live?.refreshObservations;
  const isExternalLaborLiveStream = Boolean(
    live &&
      encounterId &&
      clientId &&
      live.encounterId === encounterId &&
      live.clientId === clientId,
  );

  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const medplum = useMedplum();
  const birthDateTime = useSelector(selectBirthDateTime);
  const isOptimistic = useSelector(selectIsOptimisticBirth);
  const previousEncounterId = useRef<string | undefined>(encounterId);
  const previousClientId = useRef<string | undefined>(clientId);
  const observationsCacheRef = useRef<Map<string, Observation>>(new Map());

  useEffect(() => {
    return () => {
      observationsCacheRef.current = new Map();
    };
  }, []);

  useEffect(() => {
    if (isExternalLaborLiveStream) {
      observationsCacheRef.current = new Map();
    }
  }, [isExternalLaborLiveStream]);

  useEffect(() => {
    if (isExternalLaborLiveStream) return;
    if (
      (previousEncounterId.current && previousEncounterId.current !== encounterId) ||
      (previousClientId.current && previousClientId.current !== clientId)
    ) {
      dispatch(clearPostpartumObservations());
      observationsCacheRef.current = new Map();
    }
    previousEncounterId.current = encounterId;
    previousClientId.current = clientId;
  }, [encounterId, clientId, dispatch, isExternalLaborLiveStream]);

  const processObservationsRef = useRef<(observations: Observation[]) => Promise<void>>();

  const processObservations = useCallback(
    async (observations: Observation[]) => {
      const filtered = filterObservationsForPostpartumRedux(observations, birthDateTime);
      dispatch(loadPostpartumObservations(filtered));
    },
    [dispatch, birthDateTime],
  );

  processObservationsRef.current = processObservations;

  const {
    relatedPatientId,
    fetchRelatedPatientObservations: fetchRelatedPatientObservationsFromHelper,
  } = useRelatedPatientPostpartum({
    medplum,
    encounterId: encounterId ?? "",
    clientId: clientId ?? "",
    isOptimistic,
    enabled: !isExternalLaborLiveStream,
  });

  // The canonical (RelatedPerson-based) counterpart to relatedPatientId above -- resolves to every
  // newborn discovered for this delivery when `NewbornPatientLinking` is on (see
  // useNewbornFlowsheetSubject's own doc comment). Left unresolved on flag-off, so this never
  // duplicates the legacy relatedPatientId behavior. Ordinarily exactly one candidate; more than one
  // for a delivery with twins -- fetched and subscribed to together (OR'd into one subject filter,
  // see canonicalNewbornIdsKey below) rather than picking one, since there's no per-newborn
  // selection at this table's level and entries charted against either twin must still appear here.
  const newbornSubject = useNewbornFlowsheetSubject({
    enabled: !isExternalLaborLiveStream,
    motherId: clientId,
    laborEncounterId: encounterId,
  });
  const canonicalNewbornIds = newbornSubject.candidates.map((candidate) => candidate.patientId);
  // A stable, primitive key for refreshObservations' dependency array below -- candidates is a new
  // array reference every render, which would otherwise recreate (and re-run) refreshObservations on
  // every render even when the actual set of newborn ids is unchanged.
  const canonicalNewbornIdsKey = canonicalNewbornIds.join(",");

  const subscriptionQuery =
    !isExternalLaborLiveStream && encounterId && clientId
      ? `Observation?encounter=Encounter/${encounterId}&subject=Patient/${clientId}&_sort=-_lastUpdated`
      : null;

  useSubscription(
    subscriptionQuery || "",
    (bundle: Bundle) => {
      if (!encounterId || !clientId || isExternalLaborLiveStream) return;

      const newObservations = getResourcesFromBundle("Observation", bundle as Bundle<Observation>);

      if (newObservations.length > 0) {
        const reduxEntities = selectPostpartumObservations(store.getState());
        const allObservations = mergeIncomingObservationsIntoCache(
          newObservations,
          reduxEntities,
          observationsCacheRef,
        );
        if (processObservationsRef.current) {
          void processObservationsRef.current(allObservations);
        }
      }
    },
    {
      onError: (error: Error) => {
        if (!isOptimistic) {
          Sentry.captureException(error);
          toast({
            type: "error",
            title: "Postpartum observation stream interrupted",
            message:
              "Live updates for this encounter may be delayed. Reload the encounter or the page, then try again.",
            autoClose: false,
          });
        }
      },
    },
  );

  const relatedPatientSubscriptionQuery =
    !isExternalLaborLiveStream && encounterId && relatedPatientId
      ? `Observation?encounter=Encounter/${encounterId}&subject=Patient/${relatedPatientId}&_sort=-_lastUpdated`
      : null;

  useSubscription(
    relatedPatientSubscriptionQuery || "",
    (bundle: Bundle) => {
      if (!encounterId || isExternalLaborLiveStream) return;

      const newObservations = getResourcesFromBundle("Observation", bundle as Bundle<Observation>);

      if (newObservations.length > 0) {
        const filteredObservations = newObservations.filter((observation) =>
          observationHasSharedPostpartumRelatedCode(observation),
        );

        if (filteredObservations.length > 0) {
          const reduxEntities = selectPostpartumObservations(store.getState());
          const allObservations = mergeIncomingObservationsIntoCache(
            filteredObservations,
            reduxEntities,
            observationsCacheRef,
          );
          if (processObservationsRef.current) {
            void processObservationsRef.current(allObservations);
          }
        }
      }
    },
    {
      onError: (error: Error) => {
        if (!isOptimistic) {
          Sentry.captureException(error);
          toast({
            type: "error",
            title: "Related patient observation stream interrupted",
            message:
              "Updates for newborn postpartum flowsheet charting may be delayed. Reload the encounter or try again.",
            autoClose: false,
          });
        }
      },
    },
  );

  const canonicalNewbornSubscriptionQuery =
    !isExternalLaborLiveStream && encounterId && canonicalNewbornIdsKey
      ? `Observation?encounter=Encounter/${encounterId}&subject=${canonicalNewbornIdsKey
          .split(",")
          .map((patientId) => joinReferenceString("Patient", patientId))
          .join(",")}&_sort=-_lastUpdated`
      : null;

  useSubscription(
    canonicalNewbornSubscriptionQuery || "",
    (bundle: Bundle) => {
      if (!encounterId || isExternalLaborLiveStream) return;

      const newObservations = getResourcesFromBundle("Observation", bundle as Bundle<Observation>);

      if (newObservations.length > 0) {
        const reduxEntities = selectPostpartumObservations(store.getState());
        const allObservations = mergeIncomingObservationsIntoCache(
          newObservations,
          reduxEntities,
          observationsCacheRef,
        );
        if (processObservationsRef.current) {
          void processObservationsRef.current(allObservations);
        }
      }
    },
    {
      onError: (error: Error) => {
        if (!isOptimistic) {
          Sentry.captureException(error);
          toast({
            type: "error",
            title: "Newborn observation stream interrupted",
            message:
              "Live updates for this newborn's flowsheet entries may be delayed. Reload the encounter or the page, then try again.",
            autoClose: false,
          });
        }
      },
    },
  );

  const fetchRelatedPatientObservations = useCallback(
    async (relatedPatientIdParam: string): Promise<Observation[]> => {
      if (!encounterId || !relatedPatientIdParam) return [];
      return fetchRelatedPatientObservationsFromHelper(relatedPatientIdParam);
    },
    [encounterId, fetchRelatedPatientObservationsFromHelper],
  );

  const refreshObservations = useCallback(async (): Promise<void> => {
    if (isExternalLaborLiveStream && liveRefreshObservations) {
      await liveRefreshObservations();
      return;
    }

    if (!encounterId || !clientId) {
      return;
    }

    try {
      const observations = await searchEncounterObservationsForPatient(
        medplum,
        encounterId,
        clientId,
      );

      const relatedObservations = relatedPatientId
        ? await fetchRelatedPatientObservations(relatedPatientId)
        : [];

      const canonicalNewbornObservations = canonicalNewbornIdsKey
        ? (
            await Promise.all(
              canonicalNewbornIdsKey.split(",").map((newbornPatientId) =>
                fetchCanonicalNewbornPostpartumObservations({
                  medplum,
                  encounterId,
                  newbornPatientId,
                  isOptimistic,
                }),
              ),
            )
          ).flat()
        : [];

      const observationsFromHttp = [
        ...observations,
        ...relatedObservations,
        ...canonicalNewbornObservations,
      ];
      const reduxEntities = selectPostpartumObservations(store.getState());
      const mergedObservations = mergeIncomingObservationsIntoCache(
        observationsFromHttp,
        reduxEntities,
        observationsCacheRef,
      );
      await processObservations(mergedObservations);
    } catch (error) {
      if (!isOptimistic) {
        Sentry.captureException(error);
        toast({
          type: "error",
          title: "Could not refresh postpartum observations",
          message:
            "Charted postpartum data may be out of date. Check your connection, reload the encounter, or try again.",
          autoClose: false,
        });
      }
    }
  }, [
    isExternalLaborLiveStream,
    liveRefreshObservations,
    medplum,
    encounterId,
    clientId,
    processObservations,
    isOptimistic,
    relatedPatientId,
    fetchRelatedPatientObservations,
    canonicalNewbornIdsKey,
    store,
  ]);

  useEffect(() => {
    if (isExternalLaborLiveStream) return;
    if (!encounterId || !clientId) return;
    void refreshObservations();
  }, [encounterId, clientId, birthDateTime, refreshObservations, isExternalLaborLiveStream]);

  return {
    birthDateTime,
    refreshObservations,
  };
}
